/**
 * useFactorPreview — drives the factor-search lookup that populates a
 * StagedRow's resolved factor + alternatives.
 *
 * Used by both wizard staging surfaces (Usage Entry step + Asset
 * Activities step). Each row whose classification is complete fires a
 * single `POST /factor-search`; the response is merged back onto the
 * row via the caller's `setRows` updater. We never cancel pending
 * requests — a late response just lands on the row whose id matches.
 *
 * Behaviour mirrors what the multi-add Entry step had inline before
 * extraction: rows that have already been searched (or resolved) are
 * skipped; rows missing required fields are skipped; one search per
 * unique (rowId, classification-signature) pair.
 */

import { useCallback, useEffect, useRef } from 'react';
import {
  useFactorSearchMutation,
  type FactorSearchQuery,
} from '../api/emissions';
import { isFugitiveGas } from '@g17eco/emissions-config';
import { type AlternativeFactor } from '../types/emissions';
import { type StagedRow } from '../components/form/types';

const buildSearchQuery = (row: StagedRow): FactorSearchQuery | null => {
  const r = row.request;
  if (!r.scope || !r.activityType || !r.region || !r.unit || !r.periodStart) {
    return null;
  }
  // The wizard's combined "gas" picker stores its value on
  // `refrigerantType` regardless of kind. Split it back into the
  // route's distinct `refrigerantType` / `gasType` fields here so
  // SF6 / NF3 / PFCs route to `gasType` and don't fail the
  // refrigerant-only zod enum on `/factor-search`.
  const gasField = r.refrigerantType
    ? isFugitiveGas(r.refrigerantType)
      ? { gasType: r.refrigerantType }
      : { refrigerantType: r.refrigerantType }
    : {};
  return {
    scope: r.scope,
    activityType: r.activityType,
    region: r.region,
    unit: r.unit,
    fuelType: r.fuelType || undefined,
    vehicleType: r.vehicleType || undefined,
    ...gasField,
    processType: r.processType || undefined,
    year: new Date(r.periodStart).getFullYear() || undefined,
    regionFallback: r.regionFallback,
    yearFallback: r.yearFallback,
  };
};

export const useFactorPreview = (
  rows: StagedRow[],
  initiativeId: string,
  setRows: (updater: (prev: StagedRow[]) => StagedRow[]) => void,
) => {
  const [factorSearch] = useFactorSearchMutation();
  // Keys we've already started a search for. Cleared on classification
  // change (new key) or explicit user re-trigger via `retrigger(rowId)`
  // — the user wants to force a fresh search even though the
  // classification is unchanged (e.g. they think the resolved factor is
  // stale or want to retry after a backend hiccup).
  const startedSearchKeys = useRef(new Set<string>());

  const memoisedQuery = useCallback(buildSearchQuery, []);

  // Drop every cached key for this row so the next effect run re-fires
  // a search. Combine with clearing the row's `resolvedFactorId` /
  // `resolveAttempted` fields to fully reset the row's gates.
  const retrigger = useCallback((rowId: string) => {
    const prefix = `${rowId}:`;
    for (const key of startedSearchKeys.current) {
      if (key.startsWith(prefix)) {
        startedSearchKeys.current.delete(key);
      }
    }
  }, []);

  useEffect(() => {
    if (!initiativeId) {
      return;
    }
    const tasks: { row: StagedRow; query: FactorSearchQuery; key: string }[] = [];
    for (const row of rows) {
      if (row.resolvedFactorId || row.resolveAttempted) {
        continue;
      }
      const query = memoisedQuery(row);
      if (!query) {
        continue;
      }
      const key = `${row.id}:${JSON.stringify(query)}`;
      if (startedSearchKeys.current.has(key)) {
        continue;
      }
      startedSearchKeys.current.add(key);
      tasks.push({ row, query, key });
    }
    if (tasks.length === 0) {
      return;
    }
    for (const task of tasks) {
      void (async () => {
        const result = await factorSearch({ initiativeId, query: task.query });
        const alternatives: AlternativeFactor[] =
          'data' in result && Array.isArray(result.data) ? result.data : [];
        const top = alternatives[0];
        // Always flip `resolveAttempted` — even on backend errors —
        // otherwise the row stays stuck on "Resolving…". The caller's
        // re-trigger menu option clears this so the user can retry.
        setRows((prev) =>
          prev.map((r) =>
            r.id === task.row.id
              ? {
                  ...r,
                  resolveAttempted: true,
                  ...(top
                    ? {
                        resolvedFactorId: top.id,
                        resolvedFactorName: top.name,
                        resolvedFactorValue: top.factor ?? undefined,
                        resolvedFactorUnit: top.unit,
                        factorAlternatives: alternatives,
                      }
                    : {}),
                }
              : r,
          ),
        );
      })();
    }
  }, [rows, initiativeId, factorSearch, memoisedQuery, setRows]);

  return { buildSearchQuery: memoisedQuery, retrigger };
};
